import { getExportByIdForCustomer } from 'api/v1/export';
import { resolveExportError } from 'components/pages/DataExport/helpers';
import useInvalidateResources from 'hooks/useInvalidateResources';
import { AlertV2 } from 'components/atoms/AlertV2';
import { Button } from 'components/atoms/Button';
import {
  ExclamationIcon,
  CheckCircleIcon,
  ExclamationCircleIcon as ExclamationCircleIconSolid,
} from '@heroicons/react/solid';
import { DataExportCacheKeys } from 'types/cacheKeys.types';
import {
  useChiftExportLoadingStore,
  getChiftExportUniverseSelector,
  clearChiftExportUniverse,
  type ChiftExportUniverse,
  type ChiftExportToastContext,
} from 'store/chiftExportLoading.store';
import { isExportReadyWithIssues } from 'components/organisms/ChiftSyncForm/helpers';
import type { FC, ReactNode } from 'react';
import { useCallback } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { ExportedDocCountCacheKeys } from 'types/cacheKeys.types';
import { useTranslation } from 'react-i18next';
import { toast, type ToastOptions } from 'react-toastify';
import showToast from 'utils/toast';
import { createExpertRouteURL } from 'applications/expert/routing/navigationGenerator';
import { isWeb } from 'utils/constants';

const toastConfig: ToastOptions = {
  position: toast.POSITION.BOTTOM_LEFT,
  hideProgressBar: true,
  pauseOnHover: true,
  closeButton: false,
  autoClose: false,
};

function getExportDetailsUrl(
  ctx: ChiftExportToastContext | null,
  exportId: string | null | undefined,
): string {
  if (!ctx) return '#';
  const path =
    ctx.type === 'expense'
      ? '/expenses'
      : ctx.type === 'revenue'
      ? '/revenues'
      : '/banks';
  const base =
    !isWeb && ctx.customerId
      ? createExpertRouteURL(ctx.customerId, path)
      : path;
  const params = new URLSearchParams({ showPastExports: 'true' });
  if (exportId) params.set('exportId', exportId);
  return `${base}?${params.toString()}`;
}

type ChiftExportCompletionToastProps = {
  status: string;
  exportResult: { error?: string };
  ctx: ChiftExportToastContext;
  exportId: string | null | undefined;
};

const ChiftExportCompletionToast: FC<ChiftExportCompletionToastProps> = ({
  status,
  exportResult,
  ctx,
  exportId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const showDetailsAction = (themes: { primaryAction: string }) => (
    <div className="mt-1.5">
      <Button
        asDefault
        className={themes.primaryAction}
        onClick={() => navigate(getExportDetailsUrl(ctx, exportId))}
      >
        {t('export.status_toast.show_details')}
      </Button>
    </div>
  );

  if (status === 'failed') {
    return (
      <AlertV2
        type="error"
        variant="filled"
        size="large-mid"
        renderIcon={() => (
          <ExclamationCircleIconSolid className="w-6 h-6 text-[#DC2626]" />
        )}
        title={resolveExportError(exportResult.error)}
        actions={showDetailsAction}
        dismissible
      />
    );
  }

  if (isExportReadyWithIssues(exportResult)) {
    return (
      <AlertV2
        type="warning"
        variant="filled"
        size="large-mid"
        renderIcon={() => (
          <ExclamationIcon className="w-6 h-6 text-[#D97706]" />
        )}
        title={t('export.status_toast.ready_with_issues', {
          integration: ctx.integrationName,
          type: `${ctx.type}s`,
        })}
        actions={showDetailsAction}
        dismissible
      />
    );
  }

  return (
    <AlertV2
      type="success"
      variant="filled"
      size="large-mid"
      renderIcon={() => <CheckCircleIcon className="w-6 h-6 text-[#059669]" />}
      title={t('export.status_toast.success', {
        integration: ctx.integrationName,
        type: `${ctx.type}s`,
      })}
      actions={showDetailsAction}
      dismissible
    />
  );
};

function showChiftExportCompletionToast(
  status: string,
  exportResult: { error?: string },
  ctx: ChiftExportToastContext,
  exportId: string | null | undefined,
): void {
  toast(
    <ChiftExportCompletionToast
      status={status}
      exportResult={exportResult}
      ctx={ctx}
      exportId={exportId}
    />,
    toastConfig,
  );
}

/** Polls one universe's active export and shows completion toast when done. Uses per-request x-on-behalf-of from toastContext; x-workspace comes from axios default (stored). */
function useChiftExportPolling(
  universe: ChiftExportUniverse,
  onComplete: (
    status: string,
    exportResult: { error?: string; linkToFile?: string; _id?: string },
    ctx: ChiftExportToastContext | null,
    exportId: string | null | undefined,
  ) => void,
): void {
  const selector = getChiftExportUniverseSelector(universe);
  const { activeExportId, toastContext } = useChiftExportLoadingStore(selector);

  const customerId = toastContext?.customerId ?? '';

  useQuery(
    [DataExportCacheKeys.ExportedDataFile, 'chift-poll', activeExportId],
    // enabled: !!activeExportId && !!customerId so queryFn only runs when both are set
    () => getExportByIdForCustomer(activeExportId!, customerId),
    {
      enabled: !!activeExportId && !!customerId,
      refetchInterval: (data) => {
        const status = data?.export?.status;
        if (['failed', 'success'].includes(status as string)) return false;
        return 1000;
      },
      onSuccess: (data) => {
        const status = data?.export?.status;
        const exportResult = data?.export;
        if (!['failed', 'success'].includes(status as string)) return;
        const ctx = getChiftExportUniverseSelector(universe)(
          useChiftExportLoadingStore.getState(),
        ).toastContext;
        clearChiftExportUniverse(universe);
        onComplete(status, exportResult, ctx, activeExportId);
      },
      onError: () => {
        clearChiftExportUniverse(universe);
        showToast({ title: resolveExportError() }, { type: 'error' });
      },
    },
  );
}

/**
 * Polls for Chift export completion per universe (banks, revenues, expenses).
 * Single toast with "Show details" link to the exact export. Survives navigation
 * within customer routes (polling needs on-behalf header; provider is only mounted there).
 */
const ChiftExportProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const invalidateResources = useInvalidateResources();

  const onExportComplete = useCallback(
    (
      status: string,
      exportResult: { error?: string; linkToFile?: string; _id?: string },
      ctx: ChiftExportToastContext | null,
      exportId: string | null | undefined,
    ) => {
      invalidateResources('export');
      queryClient.invalidateQueries([
        ExportedDocCountCacheKeys.ActualExportedDocCount,
      ]);
      if (ctx) {
        const exportResourceKey =
          ctx.type === 'expense'
            ? 'exportExpenseCompleted'
            : ctx.type === 'revenue'
            ? 'exportRevenueCompleted'
            : 'exportTransactionCompleted';
        invalidateResources(exportResourceKey);
      }
      if (ctx && exportResult) {
        showChiftExportCompletionToast(status, exportResult, ctx, exportId);
      }
    },
    [invalidateResources, queryClient],
  );

  useChiftExportPolling('transaction', onExportComplete);
  useChiftExportPolling('revenue', onExportComplete);
  useChiftExportPolling('expense', onExportComplete);

  return <>{children}</>;
};

export default ChiftExportProvider;
